import { IApplicableMembershipBenefits } from "@faire/web-api/indigofair/data/IApplicableMembershipBenefits";
import { IFaireIcon } from "@faire/web-api/indigofair/data/IFaireIcon";
import { IFaireMoney } from "@faire/web-api/indigofair/data/IFaireMoney";
import { Column } from "@faire/web/slate/Layout/Column";
import { Link } from "@faire/web/ui/LinkAnchor";
import * as React from "react";

import {
  IInsiderTileBadgeRendererProps,
  InsiderTileBadgeRenderer,
} from "@faire/retailer/components/InsiderBadge/InsiderTileBadgeRenderer";

// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { BrandInfoProps } from "../ComposableProductTile/__internal__/__internal__/components/BrandInfo/brandInfoProps";
// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { ProductClickForSearchPageParameters } from "../ComposableProductTile/__internal__/__internal__/productTileActions/useOnProductClickForSearchPage";
import { ILeanProductTile } from "../MarketplaceProduct/LeanProtos/ILeanProductTile";

import { BrandMinimum } from "./__internal__/BrandMinimum";
import { InsiderLinkClientWrapper } from "./__internal__/InsiderLinkClientWrapper";
import { BrandNameText } from "./BrandNameText";
import { BrandPromoText } from "./BrandPromoText";

interface IDiscoveryProductTileBrandInfoProps {
  token: string | undefined;
  name: string | undefined;
  icon: IFaireIcon | undefined;
  minimumOrderAmount: IFaireMoney | undefined;
  insiderBenefits: IApplicableMembershipBenefits | undefined;
  insiderLabelText: string | undefined;
}

export const discoveryProductTileBrandInfoParamsILeanProductTileAdapter = (
  productTileBrand: ILeanProductTile.IBrand | undefined
): IDiscoveryProductTileBrandInfoProps => {
  return {
    token: productTileBrand?.token,
    name: productTileBrand?.name,
    icon: productTileBrand?.icon,
    minimumOrderAmount: productTileBrand?.minimum_order_amount,
    insiderBenefits: productTileBrand?.insider_benefits,
    insiderLabelText: productTileBrand?.insider_label?.text,
  };
};

export const discoveryProductTileBrandInfoParamsBrandInfoPropsAdapter = (
  props: BrandInfoProps
): IDiscoveryProductTileBrandInfoProps => {
  return {
    token: props?.brandToken,
    name: props?.brandName,
    icon: props?.brandIcon,
    minimumOrderAmount: props?.minimumOrderAmount,
    insiderBenefits: props?.insiderBadgeProps?.insiderBenefits,
    insiderLabelText: props?.insiderBadgeProps?.insiderLabelText,
  };
};
interface DiscoveryProductTileBrandInfoProps {
  brand: IDiscoveryProductTileBrandInfoProps;
  trackingParams?: ProductClickForSearchPageParameters;
  isLoggedInRetailer?: boolean;
  onBrandLinkClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  generateBrandUrl?: (brandToken: string) => string | undefined;
  dataTestId?: string | undefined;
  selectedProductOptionToken?: string;
  productUrl?: string | undefined;
  isLoaded: boolean;
  avgBrandReviewRating: string | undefined;
  brandPromoText: string | undefined;
  insiderBadgeProps: IInsiderTileBadgeRendererProps;
  isHighlightedProductForPromotionalEvent: boolean | undefined;
  onLinkClickHandler?: (ev: React.MouseEvent<HTMLAnchorElement>) => void;
}

export const DiscoveryProductTileBrandInfo: React.FC<
  DiscoveryProductTileBrandInfoProps
> = ({
  brand,
  trackingParams,
  isLoggedInRetailer,
  onBrandLinkClick,
  generateBrandUrl,
  dataTestId,
  selectedProductOptionToken,
  productUrl,
  isLoaded,
  avgBrandReviewRating,
  brandPromoText,
  insiderBadgeProps,
  isHighlightedProductForPromotionalEvent,
  onLinkClickHandler,
}) => {
  const linkContents = (
    <>
      <BrandMinimum
        minimumOrderAmount={brand.minimumOrderAmount}
        avgBrandReviewRating={avgBrandReviewRating}
        isLoggedInRetailer={isLoggedInRetailer}
      />
      <InsiderTileBadgeRenderer {...insiderBadgeProps} />
      {brandPromoText ? (
        <BrandPromoText
          promoText={brandPromoText}
          isHighlightedPromo={isHighlightedProductForPromotionalEvent}
        />
      ) : null}
    </>
  );
  return (
    <Column gap={{ mobileAndAbove: 2, tabletAndAbove: 4 }}>
      <BrandNameText
        brandToken={brand.token}
        brandIcon={brand.icon}
        brandName={brand.name}
        isLoaded={isLoaded}
        trackingParams={trackingParams}
        onBrandLinkClick={onBrandLinkClick}
        generateBrandUrl={generateBrandUrl}
      />
      {trackingParams ? (
        <InsiderLinkClientWrapper
          brandToken={brand.token}
          selectedProductOptionToken={selectedProductOptionToken}
          dataTestId={dataTestId}
          trackingParams={trackingParams}
        >
          {linkContents}
        </InsiderLinkClientWrapper>
      ) : (
        <Link
          onClick={onLinkClickHandler}
          data-test-id={dataTestId}
          to={productUrl ?? ""}
        >
          {linkContents}
        </Link>
      )}
    </Column>
  );
};
